import type { SettlementDistrictView } from '@aztec-realms/shared-types';

import type { SettlementGridBounds } from './settlement-projection';

export interface SettlementResidentPlot {
  districtCode: string;
  /** Effective, already-rotated footprint; max coordinates are exclusive corners. */
  bounds: SettlementGridBounds;
}

export interface SettlementResidentPoint {
  districtCode: string;
  x: number;
  y: number;
}

/** World-cell distances, independent of viewport, camera and quality tier. */
export const settlementResidentSpacing = 0.45;
const groundClearance = 0.18;
const maxCandidatesPerDistrict = 1024;

function fraction(index: number, base: number): number {
  let value = 0;
  let place = 1 / base;
  while (index > 0) {
    value += (index % base) * place;
    index = Math.floor(index / base);
    place /= base;
  }
  return value;
}

/**
 * Static ambient residents are presentation only. Sample actual inhabited district ground,
 * replacing blocked candidates rather than spending the population budget on failed attempts.
 * Fixed low-discrepancy candidates keep positions stable across camera/quality changes; the
 * finite search and spacing may deliberately return fewer residents when free ground is scarce.
 */
export function planSettlementResidents(
  districts: readonly Pick<SettlementDistrictView, 'code' | 'bounds' | 'unlocked'>[],
  plots: readonly SettlementResidentPlot[],
  populationBudget: number,
  preferredGround: readonly SettlementResidentPoint[] = [],
): SettlementResidentPoint[] {
  if (!Number.isFinite(populationBudget) || populationBudget <= 0) return [];
  const budget = Math.floor(populationBudget);
  const inhabited = districts
    .filter(
      ({ code, unlocked, bounds }) =>
        unlocked &&
        Object.values(bounds).every(Number.isFinite) &&
        bounds.maxX >= bounds.minX &&
        bounds.maxY >= bounds.minY &&
        plots.some((plot) => plot.districtCode === code),
    )
    .map((district) => ({
      ...district,
      plots: plots.filter((plot) => plot.districtCode === district.code),
      residents: [] as SettlementResidentPoint[],
    }))
    .sort((a, b) => a.code.localeCompare(b.code));
  const target = Math.min(
    budget,
    inhabited.reduce((sum, district) => sum + district.plots.length * 3, 0),
  );
  const result: SettlementResidentPoint[] = [];
  // Gather on verified public paths before filling remaining open ground. Still enforce
  // independent occupancy/spacing checks; preferences cannot invent population or land.
  for (const point of preferredGround.slice(0, 288)) {
    if (result.length >= target) break;
    const district = inhabited.find(({ code }) => code === point.districtCode);
    if (!district || district.residents.length >= district.plots.length * 3) continue;
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;
    const { bounds } = district;
    if (
      point.x < bounds.minX + groundClearance ||
      point.x > bounds.maxX + 1 - groundClearance ||
      point.y < bounds.minY + groundClearance ||
      point.y > bounds.maxY + 1 - groundClearance
    )
      continue;
    if (
      district.plots.some(
        ({ bounds: plot }) =>
          point.x >= plot.minX - groundClearance &&
          point.x <= plot.maxX + groundClearance &&
          point.y >= plot.minY - groundClearance &&
          point.y <= plot.maxY + groundClearance,
      )
    )
      continue;
    if (
      district.residents.some(
        (resident) =>
          Math.hypot(resident.x - point.x, resident.y - point.y) < settlementResidentSpacing,
      )
    )
      continue;
    const copy = { ...point };
    district.residents.push(copy);
    result.push(copy);
  }
  for (
    let candidate = 1;
    candidate <= maxCandidatesPerDistrict && result.length < target;
    candidate++
  ) {
    for (const district of inhabited) {
      if (result.length >= target) break;
      // Retain the existing three-residents-per-owned-building presentation ceiling locally.
      if (district.residents.length >= district.plots.length * 3) continue;
      const { bounds } = district;
      // API districts include the final cell; plots already contain exclusive ground corners.
      const point = {
        districtCode: district.code,
        x:
          bounds.minX +
          groundClearance +
          fraction(candidate, 2) * (bounds.maxX + 1 - bounds.minX - groundClearance * 2),
        y:
          bounds.minY +
          groundClearance +
          fraction(candidate, 3) * (bounds.maxY + 1 - bounds.minY - groundClearance * 2),
      };
      if (
        district.plots.some(
          ({ bounds: plot }) =>
            point.x >= plot.minX - groundClearance &&
            point.x <= plot.maxX + groundClearance &&
            point.y >= plot.minY - groundClearance &&
            point.y <= plot.maxY + groundClearance,
        )
      )
        continue;
      if (
        district.residents.some(
          (resident) =>
            Math.hypot(resident.x - point.x, resident.y - point.y) < settlementResidentSpacing,
        )
      )
        continue;
      district.residents.push(point);
      result.push(point);
    }
  }
  return result;
}
